import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import { CoArchitectLogo, ChevronRight, ImageIcon, Sparkles, User, Compass } from '../components/Icons';

/* ─── Space data ─────────────────────────────────────────────── */
interface SpaceOption {
  id: string;
  label: string;
  gradient: string;
  thumbnail: string;
}

const HOME_SPACES: SpaceOption[] = [
  {
    id: 'living-room',
    label: 'Living Room',
    gradient: 'linear-gradient(135deg, #d4a57c 0%, #7a4020 100%)',
    thumbnail: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&q=70&auto=format&fit=crop',
  },
  {
    id: 'bedroom',
    label: 'Bedroom',
    gradient: 'linear-gradient(135deg, #8baec4 0%, #2e5570 100%)',
    thumbnail: 'https://images.unsplash.com/photo-1540518614846-7eded433c457?w=400&q=70&auto=format&fit=crop',
  },
  {
    id: 'balcony',
    label: 'Balcony',
    gradient: 'linear-gradient(135deg, #7fb87f 0%, #2e5e35 100%)',
    thumbnail: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&q=70&auto=format&fit=crop',
  },
];

const CORP_SPACES: SpaceOption[] = [
  {
    id: 'conference-room',
    label: 'Conference Room',
    gradient: 'linear-gradient(135deg, #8da8c0 0%, #374a5e 100%)',
    thumbnail: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=70&auto=format&fit=crop',
  },
  {
    id: 'retail-shop',
    label: 'Retail Shop',
    gradient: 'linear-gradient(135deg, #c4a882 0%, #6b4c2a 100%)',
    thumbnail: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=70&auto=format&fit=crop',
  },
];

type Category = 'home' | 'corporate' | null;

interface RecentProject {
  id: string;
  name: string;
  originalImageUrl?: string;
  createdAt: string;
  generations?: Array<{ id: string }>;
}

/* ─── Component ──────────────────────────────────────────────── */
function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Category>(null);
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);

  const firstName = (user?.businessName || 'Designer').split(' ')[0];
  const [credits, setCredits] = useState<{ balance: number; total: number } | null>(null);

  useEffect(() => {
    axios.get(`${API}/projects`)
      .then(res => setRecentProjects(res.data.slice(0, 4)))
      .catch(() => {});
    axios.get<{ balance: string; planMonthlyCredits: number | null; totalGranted: number }>(`${API}/credits`)
      .then(res => {
        const balance = Number(res.data.balance);
        const total   = res.data.planMonthlyCredits
          ?? Math.max(res.data.totalGranted, balance);
        setCredits({ balance, total });
      })
      .catch(() => {});
  }, []);

  const handleSpaceClick = (label: string) => {
    navigate('/new-project', { state: { prefillName: label } });
  };

  const toggle = (cat: 'home' | 'corporate') => {
    setExpanded(prev => (prev === cat ? null : cat));
  };

  return (
    <div className="page-root">

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="app-header">
        <div className="header-inner">
          <div className="logo-mark-sm">
            <CoArchitectLogo size={28} />
          </div>
          <div className="header-actions-row">
            {credits !== null && (
              <button
                className={`credit-chip${credits.balance <= 1 ? ' credit-chip-low' : ''}`}
                onClick={() => navigate('/credits')}
                title="View credits"
              >
                <span className="credit-chip-dot" />
                {credits.balance}/{credits.total}
              </button>
            )}
            <button className="header-action-btn" onClick={() => navigate('/explore')} aria-label="Explore" title="Explore">
              <Compass size={18} />
            </button>
            <button className="header-action-btn" onClick={() => navigate('/profile')} aria-label="Profile" title="Profile">
              <User size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Content ────────────────────────────────────────── */}
      <div className="page-scroll">
        <div className="home-content">

          {/* Greeting */}
          <div className="home-greeting">
            <p className="home-eyebrow">Co-Architect</p>
            <h1 className="home-welcome-title">Welcome, {firstName}</h1>
            <p className="home-welcome-sub">
              Reimagine your space with AI — place any material into a real room, photorealistically, in seconds.
            </p>
          </div>

          {/* Compact credit bar */}
          {credits !== null && (
            <div className="dash-credit-card" onClick={() => navigate('/credits')} role="button" tabIndex={0}>
              <div className="dash-credit-row">
                <div className="dash-credit-label">
                  <Sparkles size={13} color="var(--accent)" />
                  {credits.balance} of {credits.total} credits left
                </div>
                <button
                  className="dash-upgrade-btn"
                  onClick={e => { e.stopPropagation(); navigate('/credits'); }}
                  type="button"
                >
                  Upgrade →
                </button>
              </div>
              <div className="credit-bar-track" style={{ height: 6 }}>
                <div
                  className={`credit-bar-fill${credits.balance <= 1 ? ' credit-bar-fill-low' : ''}`}
                  style={{ width: `${((credits.total - credits.balance) / credits.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Let's design your space */}
          <div className="home-section-header">
            <h2 className="home-section-title">Let's design your space</h2>
            <p className="home-section-sub">Pick a room type to get started</p>
          </div>

          <div className="space-cat-grid">

            {/* Home category */}
            <button
              className={`space-cat-card${expanded === 'home' ? ' space-cat-active' : ''}`}
              onClick={() => toggle('home')}
              aria-expanded={expanded === 'home'}
            >
              <div className="space-cat-visual" style={{ background: 'linear-gradient(135deg, #e8c99a 0%, #b8723a 100%)', overflow: 'hidden', position: 'relative' }}>
                <img
                  src="https://images.unsplash.com/photo-1484101403633-562f891dc89a?w=400&q=70&auto=format&fit=crop"
                  alt="Home spaces"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
                />
              </div>
              <div className="space-cat-label">
                <span>Home</span>
                <ChevronRight size={16} style={{ transform: expanded === 'home' ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }} />
              </div>
            </button>

            {/* Corporate Space category */}
            <button
              className={`space-cat-card${expanded === 'corporate' ? ' space-cat-active' : ''}`}
              onClick={() => toggle('corporate')}
              aria-expanded={expanded === 'corporate'}
            >
              <div className="space-cat-visual" style={{ background: 'linear-gradient(135deg, #9ab5cc 0%, #3a5a72 100%)', overflow: 'hidden', position: 'relative' }}>
                <img
                  src="https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=400&q=70&auto=format&fit=crop"
                  alt="Corporate spaces"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
                />
              </div>
              <div className="space-cat-label">
                <span>Corporate Space</span>
                <ChevronRight size={16} style={{ transform: expanded === 'corporate' ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }} />
              </div>
            </button>
          </div>

          {/* Sub-spaces — Home */}
          {expanded === 'home' && (
            <div className="sub-spaces-row">
              {HOME_SPACES.map(s => (
                <button key={s.id} className="sub-space-card" onClick={() => handleSpaceClick(s.label)}>
                  <div className="sub-space-visual" style={{ overflow: 'hidden', position: 'relative' }}>
                    <img
                      src={s.thumbnail}
                      alt={s.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                        (e.currentTarget.parentElement as HTMLElement).style.background = s.gradient;
                      }}
                    />
                  </div>
                  <span className="sub-space-label">{s.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Sub-spaces — Corporate */}
          {expanded === 'corporate' && (
            <div className="sub-spaces-row">
              {CORP_SPACES.map(s => (
                <button key={s.id} className="sub-space-card" onClick={() => handleSpaceClick(s.label)}>
                  <div className="sub-space-visual" style={{ overflow: 'hidden', position: 'relative' }}>
                    <img
                      src={s.thumbnail}
                      alt={s.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                        (e.currentTarget.parentElement as HTMLElement).style.background = s.gradient;
                      }}
                    />
                  </div>
                  <span className="sub-space-label">{s.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Create own project */}
          <button className="create-own-card" onClick={() => navigate('/new-project')}>
            <div className="create-own-text">
              <p className="create-own-title">Create your own project</p>
              <p className="create-own-sub">Start from scratch with a custom name</p>
            </div>
            <ChevronRight size={18} color="var(--accent)" />
          </button>

          {/* ── Your Saved Projects ──────────────────────────── */}
          <div className="home-saved-section">
            <div className="home-saved-header">
              <h2 className="home-section-title">Your Saved Projects</h2>
              <button className="home-saved-view-all" onClick={() => navigate('/library')}>
                View All
              </button>
            </div>

            {recentProjects.length === 0 ? (
              <div className="home-saved-empty">
                <div className="empty-state-icon"><ImageIcon size={24} /></div>
                <p>No projects yet — pick a space above to get started.</p>
              </div>
            ) : (
              <div className="home-saved-grid">
                {recentProjects.map(project => {
                  const count = project.generations?.length || 0;
                  return (
                    <Link key={project.id} to={`/project/${project.id}`} className="home-saved-card">
                      <div className="home-saved-thumb">
                        {project.originalImageUrl
                          ? <img src={project.originalImageUrl} alt={project.name} loading="lazy" />
                          : <div className="home-saved-thumb-placeholder"><ImageIcon size={20} /></div>
                        }
                      </div>
                      <div className="home-saved-info">
                        <p className="home-saved-name">{project.name}</p>
                        <p className="home-saved-count"><Sparkles size={10} />{count} {count === 1 ? 'preview' : 'previews'}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default Dashboard;
